
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import config from "../../config";
import { formatApiError } from "../../utilities/apiErrorMessages";
import { coalesceAsync, invalidateCoalescedAsync } from "../../utilities/coalesceAsync";
const EXPENSES_LIST_CACHE_KEY = "get-expenses:";

const extractExpensesPayload = (payload) => {
    const basePagination = {
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
    };

    if (Array.isArray(payload)) {
        const items = payload;
        return {
            items,
            pagination: {
                ...basePagination,
                per_page: items.length || basePagination.per_page,
                total: items.length,
            },
        };
    }

    if (Array.isArray(payload?.data?.data)) {
        const paginator = payload.data ?? {};
        const items = Array.isArray(paginator.data) ? paginator.data : [];
        return {
            items,
            pagination: {
                current_page: Number(paginator.current_page ?? basePagination.current_page),
                per_page: Number(paginator.per_page ?? paginator.perPage ?? (items.length || basePagination.per_page)),
                total: Number(paginator.total ?? items.length ?? basePagination.total),
                last_page: Number(paginator.last_page ?? paginator.last ?? basePagination.last_page),
            },
        };
    }

    // API sometimes returns a paginator object directly (has current_page and data array)
    if (payload && (payload.current_page || payload.per_page || payload.total || payload.last_page) && Array.isArray(payload.data)) {
        const paginator = payload;
        const items = Array.isArray(paginator.data) ? paginator.data : [];
        return {
            items,
            pagination: {
                current_page: Number(paginator.current_page ?? basePagination.current_page),
                per_page: Number(paginator.per_page ?? paginator.perPage ?? (items.length || basePagination.per_page)),
                total: Number(paginator.total ?? items.length ?? basePagination.total),
                last_page: Number(paginator.last_page ?? paginator.last ?? basePagination.last_page),
            },
        };
    }

    if (Array.isArray(payload?.data)) {
        const items = payload.data;
        return {
            items,
            pagination: {
                ...basePagination,
                per_page: items.length || basePagination.per_page,
                total: items.length,
            },
        };
    }

    return { items: [], pagination: basePagination };
};

export const GetExpenses = createAsyncThunk("expenses/GetExpenses", async ({ token, page = 1, perPage, filters = {}, trackPagination = false } = {}, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;

    try {
        const searchParams = new URLSearchParams();

        if (trackPagination) {
            const effectivePerPage = perPage ?? 15;
            searchParams.set("page", String(page ?? 1));
            searchParams.set("per_page", String(effectivePerPage));
        } else if (perPage !== undefined && perPage !== null) {
            searchParams.set("per_page", String(perPage));
        }

        Object.entries(filters).forEach(([key, value]) => {
            if (value === undefined || value === null || value === "") {
                return;
            }
            if (Array.isArray(value)) {
                if (value.length === 0) return;
                searchParams.append(key, value.join(','));
                return;
            }
            searchParams.append(key, value);
        });

        const query = searchParams.toString();
        const url = `${config.apiUrl}/expenses${query ? `?${query}` : ""}`;
        if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.debug('[GetExpenses] url=', url);
        }

        const data = await coalesceAsync(
            `get-expenses:${query}`,
            async () => {
                const response = await fetch(url, {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                const body = await response.json();
                if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
                    // eslint-disable-next-line no-console
                    console.debug('[GetExpenses] page=', page, 'perPage=', perPage, 'filters=', filters, 'response=', body);
                }

                if (!response.ok) {
                    const error = new Error(body?.message ?? 'Unable to fetch expenses');
                    error.payload = body;
                    throw error;
                }

                return body;
            }
            // In-flight only: the post-create/update/delete refetches below reuse
            // the same URL, so a cached result could resurrect a deleted row.
        );

        return fulfillWithValue(data);
    } catch (error) {
        if (error?.payload) {
            return rejectWithValue(error.payload);
        }

        return rejectWithValue({ message: error?.message ?? 'Unable to fetch expenses' });
    }
});


export const AddExpenses = createAsyncThunk("expenses/AddExpenses", async (obj, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI
    try {
        const isFormData = obj.values instanceof FormData;
        const response = await fetch(`${config.apiUrl}/expenses`, {
            method: "POST",
            body: isFormData ? obj.values : JSON.stringify(obj.values),
            headers: {
                'Accept': "application/json",
                ...(isFormData ? {} : { "Content-Type": "application/json" }),
                'Authorization': `Bearer ${obj.token}`
            }

        })

        const data = await response.json();
        if (!response.ok) {
            return rejectWithValue(data)
        } else {
            invalidateCoalescedAsync(EXPENSES_LIST_CACHE_KEY)
            return fulfillWithValue(data)
        }


    } catch (error) {
        throw rejectWithValue(error.message)
    }
})


export const UpdateExpenses = createAsyncThunk("expensesType/UpdateExpenses", async (obj, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI
    try {
        const isFormData = obj.values instanceof FormData;
        // PHP only parses multipart bodies on POST, so file updates spoof PUT.
        if (isFormData && !obj.values.get("_method")) {
            obj.values.append("_method", "PUT");
        }
        const response = await fetch(`${config.apiUrl}/expenses/${obj.id}`, {
            method: isFormData ? "POST" : "PUT",
            body: isFormData ? obj.values : JSON.stringify(obj.values),
            headers: {
                'Accept': "application/json",
                ...(isFormData ? {} : { "Content-Type": "application/json" }),
                'Authorization': `Bearer ${obj.token}`
            }
        })

        const data = await response.json();
        if (!response.ok) {
            return rejectWithValue(data)
        } else {
            invalidateCoalescedAsync(EXPENSES_LIST_CACHE_KEY)
            return fulfillWithValue(data)
        }
    } catch (error) {
        throw rejectWithValue(error.message)
    }
})

export const DeleteExpenses = createAsyncThunk("expensesType/DeleteExpenses", async (obj, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI
    try {
        const response = await fetch(`${config.apiUrl}/expenses/${obj.id}`, {
            method: "DELETE",
            headers: {
                'Accept': "application/json",
                "Content-Type": "application/json",
                'Authorization': `Bearer ${obj.token}`
            }
        })

        const data = await response.json();
        if (!response.ok) {
            return rejectWithValue(data)
        } else {
            return fulfillWithValue(data)
        }


    } catch (error) {
        throw rejectWithValue(error.message)
    }
})

export const UpdateExpenseSettings = createAsyncThunk("expenses/UpdateExpenseSettings", async ({ token, values }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI
    try {
        const response = await fetch(`${config.apiUrl}/expenses/settings`, {
            method: "PUT",
            body: JSON.stringify(values),
            headers: {
                'Accept': "application/json",
                "Content-Type": "application/json",
                'Authorization': `Bearer ${token}`
            }
        })

        const data = await response.json();
        if (!response.ok) {
            return rejectWithValue(data)
        }
        invalidateCoalescedAsync(EXPENSES_LIST_CACHE_KEY)
        return fulfillWithValue(data)
    } catch (error) {
        return rejectWithValue({ message: error?.message ?? 'Something went wrong' })
    }
})

export const ExpensesSlice = createSlice({
    name: "expenses",
    initialState: {
        expenses: [],
        paginatedExpenses: [],
        pagination: {
            current_page: 1,
            per_page: 10,
            total: 0,
            last_page: 1,
        },
        error: null,
        successMessage: null,
        loading: false,
        totalExpenses: 0,
        totalAmount: 0,
        totalAmountCurrency: "EGP",
        formulaCostsInExpenses: false,
        formulaCostsInExpensesAvailable: false,
        savingSettings: false,
        selectedExpense: null,
    },


    reducers: {
        selectExpense: (state, action) => {
            state.selectedExpense = action.payload
        },
        clearExpenseFeedback: (state) => {
            state.error = null
            state.successMessage = null
        }
    },

    extraReducers: {
        //GetExpenses actions
        [GetExpenses.pending]: (state, action) => {
            state.loading = true
            state.error = null
            state.successMessage = null
            // don't mutate items here; keep existing until fulfilled
        },
        [GetExpenses.fulfilled]: (state, action) => {
            state.loading = false

            // Normalize payload similar to ClientsSlice
            const { items, pagination } = extractExpensesPayload(action.payload?.data ?? action.payload);

            if (action.meta?.arg?.trackPagination) {
                state.paginatedExpenses = items.map((i) => i);
                state.pagination = pagination;
                const summary = action.payload?.summary;
                state.totalAmount = Number(summary?.total_amount ?? 0);
                state.totalAmountCurrency = summary?.currency ?? state.totalAmountCurrency;
                state.formulaCostsInExpenses = Boolean(summary?.formula_costs_in_expenses);
                state.formulaCostsInExpensesAvailable = Boolean(summary?.formula_costs_in_expenses_available);
            } else {
                state.expenses = items.map((i) => i);
            }

            // keep totalExpenses for backwards compatibility
            state.totalExpenses = Number(pagination?.total ?? state.totalExpenses);

            state.error = null
            state.successMessage = null
        },
        [GetExpenses.rejected]: (state, action) => {
            state.loading = false
            if (action.meta?.arg?.trackPagination) {
                state.paginatedExpenses = [];
                state.pagination = {
                    ...state.pagination,
                    total: 0,
                };
                state.totalAmount = 0;
            } else {
                state.expenses = [];
            }
            state.error = formatApiError(action.payload, 'Unable to fetch expenses')
            state.successMessage = null
        },
        [AddExpenses.pending]: (state, action) => {
            state.loading = true
            state.error = null
            state.successMessage = null
        },
        [AddExpenses.fulfilled]: (state, action) => {
            state.loading = false
            state.error = null
            state.expenses = [...state.expenses, action.payload.data]
            state.successMessage = action.payload.message
        },
        [AddExpenses.rejected]: (state, action) => {
            state.loading = false
            state.error = formatApiError(action.payload, 'Something went wrong')
            state.successMessage = null
        },

        //UpdateExpenses actions

        [UpdateExpenses.pending]: (state, action) => {
            state.loading = true
            state.error = null
            state.successMessage = null
        },
        [UpdateExpenses.fulfilled]: (state, action) => {
            state.loading = false
            state.error = null
            const index = state.expenses.findIndex(expense => expense.id === action.payload.data.id);
            state.expenses[index] = {
                ...state.expenses[index],
                ...action.payload.data,
            };


            state.successMessage = action.payload.message
        },
        [UpdateExpenses.rejected]: (state, action) => {

            state.loading = false
            state.error = formatApiError(action.payload, 'Something went wrong')
            state.successMessage = null

        },

        [UpdateExpenseSettings.pending]: (state) => {
            state.savingSettings = true
            state.error = null
            state.successMessage = null
        },
        [UpdateExpenseSettings.fulfilled]: (state, action) => {
            state.savingSettings = false
            state.formulaCostsInExpenses = Boolean(action.payload?.data?.formula_costs_in_expenses)
            state.successMessage = action.payload?.message ?? null
        },
        [UpdateExpenseSettings.rejected]: (state, action) => {
            state.savingSettings = false
            state.error = formatApiError(action.payload, 'Something went wrong')
        },

        [DeleteExpenses.pending]: (state, action) => {
            state.loading = true
            state.error = null
            state.successMessage = null
        },
        [DeleteExpenses.fulfilled]: (state, action) => {
            state.loading = false
            state.error = null
            let index = state.expenses.findIndex(({ id }) => id === action.payload.data?.id);
            state.expenses.splice(index, 1);
            state.successMessage = action.payload.message
        },
        [DeleteExpenses.rejected]: (state, action) => {

            state.loading = false
            state.error = formatApiError(action.payload, 'Something went wrong')
            state.successMessage = null

        },
    }
})

export default ExpensesSlice.reducer;
export const { selectExpense, clearExpenseFeedback } = ExpensesSlice.actions